/**
 * Viewport / resize / zoom timeline events for Ryn Replay (#30070).
 *
 * Zoom is derived from `visualViewport.scale` when present, otherwise from
 * `devicePixelRatio` changes — the practical signal browsers expose without
 * a dedicated zoom API.
 */

import { RYN_REPLAY_VIEWPORT_EVENT_TYPE } from '@ups/xplat/base/ryn-replay';

export { RYN_REPLAY_VIEWPORT_EVENT_TYPE };

export type RynReplayViewportEmit = (
  type: string,
  payload: Record<string, unknown>
) => void;

export interface IRynReplayViewportSnapshot {
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  visualViewportScale?: number;
}

export interface IRynReplayViewportCapture {
  install(): void;
  uninstall(): void;
}

export const readRynReplayViewportSnapshot = (
  win: Window = globalThis as unknown as Window
): IRynReplayViewportSnapshot => {
  const vv = (
    win as Window & {
      visualViewport?: { scale?: number };
    }
  ).visualViewport;
  return {
    innerWidth: win.innerWidth ?? 0,
    innerHeight: win.innerHeight ?? 0,
    devicePixelRatio: win.devicePixelRatio ?? 1,
    ...(typeof vv?.scale === 'number' ? { visualViewportScale: vv.scale } : {}),
  };
};

export class RynReplayViewportCapture implements IRynReplayViewportCapture {
  private installed = false;
  private resizeHandler?: () => void;
  private vvHandler?: () => void;
  private lastJson = '';

  constructor(
    private readonly emit: RynReplayViewportEmit,
    private readonly win: Window = globalThis as unknown as Window
  ) {}

  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;
    this.publish(true);
    this.resizeHandler = () => this.publish(false);
    this.win.addEventListener?.('resize', this.resizeHandler);
    const vv = (
      this.win as Window & {
        visualViewport?: {
          addEventListener?: (type: string, fn: () => void) => void;
        };
      }
    ).visualViewport;
    if (vv?.addEventListener) {
      this.vvHandler = () => this.publish(false);
      vv.addEventListener('resize', this.vvHandler);
      vv.addEventListener('scroll', this.vvHandler);
    }
  }

  uninstall(): void {
    if (!this.installed) {
      return;
    }
    this.installed = false;
    if (this.resizeHandler) {
      this.win.removeEventListener?.('resize', this.resizeHandler);
    }
    const vv = (
      this.win as Window & {
        visualViewport?: {
          removeEventListener?: (type: string, fn: () => void) => void;
        };
      }
    ).visualViewport;
    if (this.vvHandler && vv?.removeEventListener) {
      vv.removeEventListener('resize', this.vvHandler);
      vv.removeEventListener('scroll', this.vvHandler);
    }
    this.resizeHandler = undefined;
    this.vvHandler = undefined;
    this.lastJson = '';
  }

  private publish(force: boolean): void {
    try {
      const snapshot = readRynReplayViewportSnapshot(this.win);
      const json = JSON.stringify(snapshot);
      if (!force && json === this.lastJson) {
        return;
      }
      this.lastJson = json;
      this.emit(
        RYN_REPLAY_VIEWPORT_EVENT_TYPE,
        snapshot as unknown as Record<string, unknown>
      );
    } catch {
      // never break the host
    }
  }
}
